/**
 * BlogIndex — Public blog listing page for Searchline
 * Route: /blog (no auth required)
 *
 * Fetches all published articles from radar_articles Supabase table
 * and displays them as a card grid.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { createClient } from '@supabase/supabase-js'
import searchlineLogo from '@/assets/searchline-logo.jpg'

const supabase = createClient(
  'https://xwcmvemayjjcfyjhdkii.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh3Y212ZW1heWpqY2Z5amhka2lpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ3OTAyNzMsImV4cCI6MjA5MDM2NjI3M30.Ej0O6Lh8QI8g5VbDjlPw1aBpcuWr2qvxYZrQREL6vgo'
)

interface Article {
  id: string
  title: string
  slug: string
  summary: string
  keywords: string[]
  image_url?: string
  created_at: string
}

export function BlogIndex() {
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchArticles() {
      const { data, error } = await supabase
        .from('radar_articles')
        .select('id, title, slug, summary, keywords, image_url, created_at')
        .eq('status', 'published')
        .order('created_at', { ascending: false })

      if (error) {
        setError(error.message)
      } else {
        setArticles(data ?? [])
      }
      setLoading(false)
    }
    fetchArticles()
  }, [])

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#0f172a]/90 backdrop-blur border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={searchlineLogo} alt="Searchline" className="w-8 h-8 rounded-lg" />
            <div>
              <span className="text-white font-bold text-lg tracking-tight">Searchline</span>
              <span className="block text-[#f97316] font-medium text-xs">AI Hiring</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-white/50 hover:text-white text-sm transition-colors"
            >
              Home
            </Link>
            <Link
              to="/login"
              className="bg-[#f97316] hover:bg-[#ea6c0a] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="max-w-6xl mx-auto px-5 pt-16 pb-10">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[#f97316] text-sm font-semibold uppercase tracking-widest">Blog</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">
          Searchline Blog —{' '}
          <span className="text-[#f97316]">AI Recruitment Insights</span>
        </h1>
        <p className="text-white/60 text-lg max-w-2xl">
          Practical insights, research, and thinking on the future of hiring — written by the Searchline team.
        </p>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-5 pb-24">
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#f97316] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-24 text-red-400">
            <p className="text-lg font-medium mb-2">Something went wrong</p>
            <p className="text-sm text-white/40">{error}</p>
          </div>
        )}

        {!loading && !error && articles.length === 0 && (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-full bg-[#f97316]/10 border border-[#f97316]/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📝</span>
            </div>
            <p className="text-white/50 text-lg">No articles yet.</p>
            <p className="text-white/30 text-sm mt-1">Check back soon — we're writing!</p>
          </div>
        )}

        {!loading && !error && articles.length > 0 && (
          <div className="space-y-8">
            {articles.map((article, idx) => (
              <ArticleCard key={article.id} article={article} featured={idx === 0} />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-white/30 text-sm">© {new Date().getFullYear()} Searchline. All rights reserved.</span>
          <Link to="/" className="text-white/30 hover:text-white/60 text-sm transition-colors">
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  )
}

function ArticleCard({ article, featured = false }: { article: Article; featured?: boolean }) {
  const date = new Date(article.created_at).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  })

  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group block bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:border-[#f97316]/40 transition-all duration-200"
    >
      {/* Image — inline style so it never gets cropped */}
      {article.image_url && (
        <div style={{ width: '100%', background: '#0a0f1e', lineHeight: 0 }}>
          <img
            src={article.image_url}
            alt={article.title}
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </div>
      )}

      <div className={featured ? 'p-6 sm:p-8' : 'p-6'}>
        <span className="text-white/30 text-xs font-medium">{date}</span>

        <h2 className={`mt-2 font-bold text-white leading-tight group-hover:text-[#f97316] transition-colors ${
          featured ? 'text-2xl sm:text-3xl' : 'text-xl'
        }`}>
          {article.title}
        </h2>

        {article.summary && (
          <p className={`mt-3 text-white/60 leading-relaxed ${
            featured ? 'text-base line-clamp-2' : 'text-sm line-clamp-3'
          }`}>
            {article.summary}
          </p>
        )}

        {article.keywords && article.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {article.keywords.slice(0, 4).map((kw) => (
              <span key={kw} className="text-xs px-2.5 py-1 rounded-full bg-[#f97316]/10 text-[#f97316] border border-[#f97316]/20 font-medium">
                {kw}
              </span>
            ))}
            {article.keywords.length > 4 && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-white/30 border border-white/10">
                +{article.keywords.length - 4}
              </span>
            )}
          </div>
        )}

        <span className="mt-4 inline-flex items-center gap-1.5 text-[#f97316] text-sm font-semibold">
          Read more →
        </span>
      </div>
    </Link>
  )
}
